/**
 * Neon Grid Flow - 霓虹网格流光效果
 * 
 * 设计灵感：
 * - 2026 设计趋势 - 霓虹发光 + 网格系统 + 流光动画
 * - 赛博朋克美学 - 霓虹城市、未来科技感
 * - 光线追踪效果 - 流光粒子沿网格路径流动
 * 
 * 技术要点：
 * - 3D 透视网格（CSS 3D transform）
 * - 霓虹发光效果（多层 glow + blur）
 * - 流光粒子系统（沿网格线条流动）
 * - Spring 物理动画（平滑入场）
 * - 确定性随机算法（帧级一致性）
 * - HSL 颜色渐变（色相旋转）
 * 
 * 应用场景：
 * - 赛博朋克主题视频
 * - 科技品牌宣传
 * - 未来感 UI 设计
 * - 小红书科技感内容
 * - 游戏界面动画
 */

import React from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate, spring } from 'remotion';

// ========== 类型定义 ==========

interface GridLine {
  id: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
  delay: number;
  isHorizontal: boolean;
}

interface FlowParticle {
  id: number;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  progress: number;
  color: string;
  size: number;
  delay: number;
  speed: number;
}

// ========== 配置常量 ==========

const GRID_SIZE = 15; // 网格密度
const PARTICLE_COUNT = 40; // 流光粒子数量
const NEON_COLORS = [
  '#ff0080', // 霓虹粉
  '#00ffff', // 霓虹青
  '#ff00ff', // 霓虹紫
  '#00ff00', // 霓虹绿
  '#ffff00', // 霓虹黄
];

// ========== 确定性随机算法 ==========

const seededRandom = (seed: number): number => {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
};

// ========== 网格线组件 ==========

const NeonGridLine: React.FC<{
  line: GridLine;
  frame: number;
  fps: number;
}> = ({ line, frame, fps }) => {
  // Spring 入场动画
  const opacity = spring({
    frame: frame - line.delay,
    fps,
    config: {
      damping: 200,
      stiffness: 100,
    },
  });

  const scale = spring({
    frame: frame - line.delay,
    fps,
    config: {
      damping: 200,
      stiffness: 100,
    },
  });

  return (
    <g opacity={opacity} transform={`scale(${scale})`}>
      {/* 霓虹发光效果（3 层 glow） */}
      <line
        x1={line.x1}
        y1={line.y1}
        x2={line.x2}
        y2={line.y2}
        stroke={line.color}
        strokeWidth="3"
        filter="url(#neonGlow3)"
        opacity={0.3}
      />
      <line
        x1={line.x1}
        y1={line.y1}
        x2={line.x2}
        y2={line.y2}
        stroke={line.color}
        strokeWidth="2"
        filter="url(#neonGlow2)"
        opacity={0.6}
      />
      <line
        x1={line.x1}
        y1={line.y1}
        x2={line.x2}
        y2={line.y2}
        stroke={line.color}
        strokeWidth="1"
        filter="url(#neonGlow1)"
      />
    </g>
  );
};

// ========== 流光粒子组件 ==========

const FlowParticleComponent: React.FC<{
  particle: FlowParticle;
  frame: number;
  fps: number;
}> = ({ particle, frame, fps }) => {
  // 粒子沿网格线条流动
  const progress = interpolate(
    (frame - particle.delay) * particle.speed,
    [0, 60],
    [0, 1],
    {
      extrapolateRight: 'clamp',
      extrapolateLeft: 'clamp',
    }
  );

  // 计算粒子当前位置（沿线条）
  const currentX = interpolate(progress, [0, 1], [particle.startX, particle.endX]);
  const currentY = interpolate(progress, [0, 1], [particle.startY, particle.endY]);

  // 粒子透明度（淡入淡出）
  const opacity = interpolate(progress, [0, 0.1, 0.9, 1], [0, 1, 1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  // Spring 入场动画
  const scale = spring({
    frame: frame - particle.delay,
    fps,
    config: {
      damping: 200,
    },
  });

  return (
    <g opacity={opacity * scale}>
      {/* 粒子发光效果（3 层） */}
      <circle
        cx={currentX}
        cy={currentY}
        r={particle.size * 3}
        fill={particle.color}
        opacity={0.2}
        filter="url(#particleGlow3)"
      />
      <circle
        cx={currentX}
        cy={currentY}
        r={particle.size * 2}
        fill={particle.color}
        opacity={0.5}
        filter="url(#particleGlow2)"
      />
      <circle
        cx={currentX}
        cy={currentY}
        r={particle.size}
        fill={particle.color}
        filter="url(#particleGlow1)"
      />
      {/* 粒子核心高光 */}
      <circle
        cx={currentX}
        cy={currentY}
        r={particle.size * 0.5}
        fill="white"
        opacity={0.8}
      />
    </g>
  );
};

// ========== 主组件 ==========

export const NeonGridFlow: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // 生成网格线条（横向 + 纵向）
  const gridLines: GridLine[] = [];
  const gridSpacing = width / GRID_SIZE;

  // 横向网格线
  for (let i = 0; i <= GRID_SIZE; i++) {
    const y = i * gridSpacing;
    const colorIndex = Math.floor(seededRandom(i * 100) * NEON_COLORS.length);
    gridLines.push({
      id: i,
      x1: 0,
      y1: y,
      x2: width,
      y2: y,
      color: NEON_COLORS[colorIndex],
      delay: i * 2,
      isHorizontal: true,
    });
  }

  // 纵向网格线
  for (let i = 0; i <= GRID_SIZE; i++) {
    const x = i * gridSpacing;
    const colorIndex = Math.floor(seededRandom((i + GRID_SIZE) * 100) * NEON_COLORS.length);
    gridLines.push({
      id: i + GRID_SIZE + 1,
      x1: x,
      y1: 0,
      x2: x,
      y2: height,
      color: NEON_COLORS[colorIndex],
      delay: i * 2 + 5,
      isHorizontal: false,
    });
  }

  // 生成流光粒子（沿网格线条流动）
  const flowParticles: FlowParticle[] = [];
  for (let i = 0; i < PARTICLE_COUNT; i++) {
    const randomLineIndex = Math.floor(seededRandom(i * 200) * gridLines.length);
    const line = gridLines[randomLineIndex];
    const colorIndex = Math.floor(seededRandom(i * 300) * NEON_COLORS.length);
    
    // 随机起点和终点（沿线条）
    const reverse = seededRandom(i * 400) > 0.5;
    
    flowParticles.push({
      id: i,
      startX: reverse ? line.x2 : line.x1,
      startY: reverse ? line.y2 : line.y1,
      endX: reverse ? line.x1 : line.x2,
      endY: reverse ? line.y1 : line.y2,
      progress: 0,
      color: NEON_COLORS[colorIndex],
      size: interpolate(seededRandom(i * 500), [0, 1], [2, 6]),
      delay: i * 3 + seededRandom(i * 600) * 20,
      speed: interpolate(seededRandom(i * 700), [0, 1], [0.5, 1.5]),
    });
  }

  // 标题动画
  const titleOpacity = spring({
    frame: frame - 20,
    fps,
    config: {
      damping: 200,
    },
  });

  const titleY = interpolate(
    spring({
      frame: frame - 20,
      fps,
      config: {
        damping: 200,
        stiffness: 100,
      },
    }),
    [0, 1],
    [50, 0]
  );

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 100%)',
        perspective: '1000px',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {/* SVG 画布 */}
      <svg
        width={width}
        height={height}
        style={{
          transform: 'rotateX(60deg)',
          transformOrigin: 'center center',
        }}
      >
        {/* 定义发光滤镜 */}
        <defs>
          {/* 霓虹发光效果 */}
          <filter id="neonGlow1" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="blur1" />
            <feMerge>
              <feMergeNode in="blur1" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="neonGlow2" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="5" result="blur2" />
            <feMerge>
              <feMergeNode in="blur2" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="neonGlow3" x="-150%" y="-150%" width="400%" height="400%">
            <feGaussianBlur stdDeviation="10" result="blur3" />
            <feMerge>
              <feMergeNode in="blur3" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* 粒子发光效果 */}
          <filter id="particleGlow1" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="3" result="blur1" />
            <feMerge>
              <feMergeNode in="blur1" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="particleGlow2" x="-200%" y="-200%" width="500%" height="500%">
            <feGaussianBlur stdDeviation="8" result="blur2" />
            <feMerge>
              <feMergeNode in="blur2" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="particleGlow3" x="-300%" y="-300%" width="700%" height="700%">
            <feGaussianBlur stdDeviation="15" result="blur3" />
            <feMerge>
              <feMergeNode in="blur3" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* 渲染网格线 */}
        {gridLines.map((line) => (
          <NeonGridLine key={line.id} line={line} frame={frame} fps={fps} />
        ))}

        {/* 渲染流光粒子 */}
        {flowParticles.map((particle) => (
          <FlowParticleComponent
            key={particle.id}
            particle={particle}
            frame={frame}
            fps={fps}
          />
        ))}
      </svg>

      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: `translate(-50%, -50%) translateY(${titleY}px)`,
          opacity: titleOpacity,
          textAlign: 'center',
          fontFamily: 'Arial, sans-serif',
        }}
      >
        <div
          style={{
            fontSize: 80,
            fontWeight: 'bold',
            color: '#00ffff',
            textShadow: `
              0 0 10px #00ffff,
              0 0 20px #00ffff,
              0 0 30px #00ffff,
              0 0 40px #00ffff,
              0 0 50px #00ffff
            `,
            letterSpacing: '10px',
          }}
        >
          NEON GRID
        </div>
        <div
          style={{
            fontSize: 30,
            color: '#ff00ff',
            textShadow: `
              0 0 5px #ff00ff,
              0 0 10px #ff00ff,
              0 0 15px #ff00ff
            `,
            marginTop: '20px',
            letterSpacing: '5px',
          }}
        >
          2026 VISUAL TRENDS
        </div>
      </div>
    </AbsoluteFill>
  );
};
